/* @jsx h */
import {
  defineComponent,
  h,
  ref,
  computed,
  watch,
  nextTick,
  onBeforeUnmount,
} from "vue";
import { useLocalization } from "@lemmonhq/uikit";

/**
 * No animation library here on purpose.
 *
 * Every motion in this component is a CSS transition on `transform` (and the
 * scrims' `opacity`), which the browser hands to the compositor. A JS tween —
 * anime.js or otherwise — writes `style.transform` from a callback on every
 * frame, which pins the animation to the main thread: a Vue re-render, an image
 * decode or a GC pause anywhere in the app stalls the card mid-flight. On a
 * phone, with a full-bleed photograph to recomposite, that was the difference
 * between smooth and janky.
 *
 * The trade is that CSS has no spring solver. `RETURN_EASE` is a back-out curve
 * standing in for one, which is what the spring was there to provide.
 */

/**
 * A swipeable stack of passes, the way a deck of cards behaves in the hand.
 *
 * Used at every width. A vertical list of full-height passes turns the wallet
 * into a scroll and the cards stop reading as a set you own — a deck you flick
 * through is the point of the page, not a small-screen compromise.
 *
 * The stack never empties. Swiping the front card sends it to the back rather
 * than removing it, so the deck loops — there is no terminal state to design,
 * and no way to lose sight of a card you still hold.
 *
 * Every slot's transform is written imperatively during a gesture and cleared
 * afterwards, so the resting positions come from one place (the style binding
 * below) and a gesture can never leave a card parked on top of another.
 */

/** Past this, a release is a throw rather than a nudge back into place. */
const DISMISS_DISTANCE = 110;
/** …or fast enough that distance stops mattering. A flick is short and quick. */
const DISMISS_VELOCITY = 0.55;
/** Below this, the gesture was a tap. */
const TAP_SLOP = 8;

/**
 * Where each card behind the front one sits.
 *
 * A pile of cards on a table is never a neat column — they fan, they sit at
 * angles, corners stick out on both sides. A pure vertical offset reads as a UI
 * affordance stacked in a list; a bit of rotation and sideways drift reads as
 * something you could pick up. The values are fixed rather than random so a
 * card keeps its angle between renders instead of twitching on every paint.
 *
 * The offsets also have to beat the shrink: scaling about the centre pulls the
 * edges *in* by `size × scale / 2`, so too small an offset leaves the card
 * behind entirely hidden by the one in front.
 */
const DEPTH_SCATTER = [
  { x: 0, y: 0, rotate: 0 },
  { x: -10, y: 14, rotate: -5.5 },
  { x: 9, y: 24, rotate: 4.5 },
  { x: -5, y: 33, rotate: 2.5 },
];
const DEPTH_SCALE = 0.025;
const DEPTH_DIM = 0.1;
/** Cards drawn behind the front one. Past three the rest would be invisible. */
const VISIBLE_DEPTH = 3;

/** Tilt per pixel dragged, and the ceiling on it. */
const ROTATE_PER_PX = 1 / 14;
const MAX_ROTATE = 18;

/**
 * How far the card is allowed off the horizontal.
 *
 * A card that follows the finger exactly can be dragged anywhere, and the
 * gesture stops reading as "deal this card off the top" — it reads as dragging
 * an object around a canvas. Constraining it to a shallow band is most of what
 * makes the Tinder swipe feel deliberate: the horizontal axis tracks the finger
 * one to one, the vertical barely moves, and the arc comes from rotating about
 * a pivot below the card rather than from the finger's own path.
 */
const VERTICAL_DAMPING = 0.25;
const MAX_VERTICAL = 56;

/**
 * Snappy with a little overshoot: the card should feel sprung, not slid.
 *
 * A back-out curve rather than a real spring solver. The spring existed for the
 * overshoot, and this gives the same shape while running on the compositor —
 * see `moveFront`.
 */
const RETURN_MS = 420;
const RETURN_EASE = "cubic-bezier(0.34, 1.4, 0.64, 1)";

/** How long a thrown card takes to clear the screen. */
const THROW_MS = 320;

/** The card's own turn-over. Matches `duration-500` on the face wrapper. */
const FLIP_MS = 500;

/**
 * The scatter at a possibly fractional depth.
 *
 * Fractional because the drag cascade slides every card one step forward as the
 * front one leaves — a card halfway between depth 2 and depth 1 has to sit
 * halfway between their angles too, or it would snap rather than travel.
 */
const scatterAt = (depth: number) => {
  const clamped = Math.max(0, Math.min(depth, DEPTH_SCATTER.length - 1));
  const lower = Math.floor(clamped);
  const upper = Math.min(lower + 1, DEPTH_SCATTER.length - 1);
  const t = clamped - lower;
  const a = DEPTH_SCATTER[lower];
  const b = DEPTH_SCATTER[upper];
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    rotate: a.rotate + (b.rotate - a.rotate) * t,
  };
};

const depthTransform = (depth: number) => {
  const s = scatterAt(depth);
  return `translate3d(${s.x}px, ${s.y}px, 0) rotate(${s.rotate}deg) scale(${
    1 - Math.max(depth, 0) * DEPTH_SCALE
  })`;
};

/**
 * How dark the scrim over a card at this depth is.
 *
 * This used to be `filter: brightness()` on the slot. A filter forces the whole
 * card — photograph, both 3D faces, every stamp chip — to be rendered into an
 * offscreen buffer and re-filtered whenever anything about it changes, once per
 * card per frame. A black overlay at the same effective darkness is one solid
 * rectangle the compositor can cache and fade, and it looks the same.
 */
const depthDim = (depth: number) => Math.max(depth, 0) * DEPTH_DIM;

/** Behind cards move under their own CSS transition, staggered by depth. */
const DEPTH_TRANSITION =
  "transform 320ms cubic-bezier(0.16, 1, 0.3, 1), opacity 320ms";

export type WalletCardStackProps = {
  /**
   * Rendered front-first; the deck wraps around this order. A card with a
   * `renderBack` flips when tapped instead of reporting a selection — the code
   * is on the back of the pass, which is where you would turn a real one over
   * to find it.
   */
  items: {
    key: string;
    /**
     * `isFront` is passed so a card can spend any expensive effect only where
     * it can be seen — behind cards are scaled, dimmed and largely covered.
     * Nothing in the wallet needs it today; the footer used to frost itself on
     * the front card alone and that was worse than not frosting at all, since
     * the effect came and went as the deck moved.
     */
    render: (isFront: boolean) => any;
    renderBack?: () => any;
  }[];
  /** Tap on a card with no back. Not fired when the gesture was a drag. */
  onSelect?: (key: string) => void;
  /** The front card changed, so the action row can follow it. */
  onFrontChange?: (key: string) => void;
  /**
   * Bring the card with this key to the front once, when it first appears.
   *
   * Used for the deep link the wallet-pass email carries: land the reader on the
   * membership they were just enrolled in rather than on whatever card happens
   * to sort first. It fires only when the key changes to something new, so a
   * later data refresh cannot yank a reader off a card they have since swiped to.
   */
  focusKey?: string | null;
  /**
   * Take the height the parent gives instead of choosing one.
   *
   * On a phone the page is locked to the viewport and the card is whatever is
   * left after the chrome above and below it, so the stack has to measure
   * rather than declare. Declaring was the old behaviour — `68vh` with a 420px
   * floor — and both halves of that are wrong on a phone: `vh` is the *large*
   * viewport, so it ignores Android's address bar and overflows whenever the
   * bar is showing, and the floor overflows a short screen outright.
   */
  fill?: boolean;
  /** Width cap for the card itself; defaults to 460px. Navigation stays outside it. */
  maxCardWidth?: number;
};

export const WalletCardStack = defineComponent<WalletCardStackProps>({
  name: "WalletCardStack",
  props: [
    "items",
    "onSelect",
    "onFrontChange",
    "focusKey",
    "fill",
    "maxCardWidth",
  ],
  setup(props) {
    const { t } = useLocalization();

    /** Rotation offset into `items`; the card at `front` is on top. */
    const front = ref(0);
    /** Whether the front card is showing its back. Reset as the deck moves. */
    const flipped = ref(false);

    /**
     * Whether the card is currently a 3D object at all.
     *
     * `perspective`, `transform-style: preserve-3d` and the back face used to be
     * permanent, on every slot. That is expensive in a way that has nothing to do
     * with flipping: a `preserve-3d` subtree cannot be flattened into a single
     * cached texture, so the browser keeps the 3D rendering context live and gives
     * every `backface-visibility: hidden` face its own render surface. Dragging
     * such a card moves a small scene, not one bitmap.
     *
     * So the 3D machinery is mounted only for the turn itself. It outlives
     * `flipped` by the length of the animation, because the flip *back* needs the
     * context it is rotating in — drop it on the same tick and the card would
     * snap flat instead of turning over.
     */
    const flip3d = ref(false);
    let flipTimer: ReturnType<typeof setTimeout> | undefined;
    const setFlipped = (value: boolean) => {
      clearTimeout(flipTimer);
      if (value) {
        flip3d.value = true;
        flipped.value = true;
        return;
      }
      flipped.value = false;
      flipTimer = setTimeout(() => (flip3d.value = false), FLIP_MS + 60);
    };

    /**
     * Deal a named card to the top, at most once per key.
     *
     * Watched rather than done in `onMounted` because the items arrive after the
     * async fetch, so the card to focus usually does not exist yet at mount. The
     * `lastFocused` guard is what keeps this a one-time landing: without it a
     * re-render (or a data refresh) would re-run the effect and pull the reader
     * back to the deep-linked card after they had swiped past it. Registered here
     * rather than beside `front` so `setFlipped` above is already initialised
     * when the immediate run fires.
     */
    let lastFocused: string | null | undefined;
    const focusTo = (key?: string | null) => {
      if (!key || key === lastFocused) return;
      const idx = props.items.findIndex((it) => it.key === key);
      if (idx < 0) return;
      lastFocused = key;
      front.value = idx;
      setFlipped(false);
      props.onFrontChange?.(props.items[idx].key);
    };
    watch(
      [() => props.focusKey, () => props.items.map((it) => it.key).join("|")],
      () => focusTo(props.focusKey),
      { immediate: true },
    );

    /**
     * One element per stack *slot*, not per card. Cards move through the slots
     * as the deck rotates, so the front element is always the same node and its
     * inline transform is ours alone — keying by card instead would recycle a
     * node still carrying the transform that threw it off-screen.
     */
    /** The depth scrims, addressed alongside their slots. */
    const dimEls: (HTMLElement | null)[] = [];
    const setDim = (depth: number) => (el: unknown) => {
      dimEls[depth] = (el ?? null) as HTMLElement | null;
    };
    /** Writes a scrim's darkness without touching the card beneath it. */
    const paintDim = (depth: number, value: number, transition?: string) => {
      const el = dimEls[depth];
      if (!el) return;
      if (transition !== undefined) el.style.transition = transition;
      el.style.opacity = String(value);
    };

    const slotEls: (HTMLElement | null)[] = [];
    const setSlot = (depth: number) => (el: unknown) => {
      slotEls[depth] = (el ?? null) as HTMLElement | null;
    };

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let dx = 0;
    let dy = 0;
    let moved = false;
    let pointerId: number | null = null;
    let animating = false;
    /**
     * +1 when the card was picked up above its middle, -1 below.
     *
     * A card held near the top swings the far end out as you pull it sideways; a
     * card held near the bottom swings the other way. Tying the tilt to where it
     * was grabbed is what makes the card feel like it is being held rather than
     * animated.
     */
    let grabSign = 1;

    /**
     * Where the card is allowed to be for the current gesture.
     *
     * One place decides the whole path, so the drag, the spring back and the
     * throw cannot disagree about it.
     */
    const dragPath = () => ({
      x: dx,
      y: Math.max(-MAX_VERTICAL, Math.min(MAX_VERTICAL, dy * VERTICAL_DAMPING)),
      rotate: Math.max(
        -MAX_ROTATE,
        Math.min(MAX_ROTATE, dx * ROTATE_PER_PX * grabSign),
      ),
    });

    const count = computed(() => props.items.length);
    const slotCount = computed(() =>
      Math.min(VISIBLE_DEPTH + 1, Math.max(count.value, 1)),
    );

    /** The card sitting at a given depth, wrapping so the deck is endless. */
    const cardAt = (depth: number) =>
      props.items[(front.value + depth) % count.value];

    /**
     * Hands every slot back to its resting style binding.
     *
     * The one rule that keeps the stagger honest: after any gesture the only
     * thing positioning a card is the binding below. Leaving an inline
     * transform behind is what previously parked the new front card on the
     * exact spot of the card behind it, and the stack looked flat from the
     * second card on.
     */
    const resetSlots = () => {
      for (let d = 0; d < slotCount.value; d++) {
        const el = slotEls[d];
        if (!el) continue;
        // Suppressed for this frame: a slot must *be* at its resting depth the
        // instant the deck moves on, not transition into it. Clearing the
        // transform and waiting for the binding to re-apply animates the card
        // behind from dead centre every swipe, which reads as it sliding out
        // from under the new front card.
        el.style.transition = "none";
        el.style.opacity = "";
        el.style.transform = d === 0 ? "" : depthTransform(d);
        paintDim(d, d === 0 ? 0 : depthDim(d), "none");
        // Force the reset to land before transitions are allowed back.
        void el.offsetHeight;
        el.style.transition = "";
        paintDim(d, d === 0 ? 0 : depthDim(d), "");
      }
      // Everything is at rest, so the promoted layers can go back.
      disarmGesture();
    };

    const advance = () => {
      front.value = (front.value + 1) % count.value;
      // A new card always arrives face up; carrying the flip over would show
      // the next member's code without them asking for it.
      setFlipped(false);
      props.onFrontChange?.(cardAt(0).key);
      // The card behind has already risen into place during the drag, so there
      // is nothing to animate here — just let the bindings take back over.
      //
      // After the re-render, never before it. Cards travel *through* the slots,
      // so changing `front` means Vue is about to swap the contents of every
      // slot — and until it does, slot 0 still holds the card that was just
      // thrown off screen. Resetting the transforms first therefore parks the
      // outgoing card back in the middle of the deck for as long as the render
      // takes, which reads as a card appearing and then being replaced by
      // another one. `nextTick` lands after the patch and still before the
      // browser paints, so the swap is never visible.
      nextTick(() => {
        resetSlots();
        animating = false;
      });
    };

    /**
     * Tells the compositor a gesture is coming, *before* the first move.
     *
     * This is the single biggest cost in a drag, and it is invisible in the
     * code that does the dragging. A CSS transition is promoted to its own
     * compositor layer automatically for as long as it runs — which is why the
     * throw and the spring back are smooth. A drag is not a transition: it
     * writes `style.transform` from a pointer handler, and that gets no
     * promotion at all. At rest the front card has no layer, so the first
     * `paintDrag` forces one to be built right then — rasterising a full-bleed
     * photograph, a gradient scrim and ten stamp chips at device pixel ratio,
     * mid-gesture. The transform is cleared at rest, the
     * layer is discarded, and the whole cost is paid again on the next drag.
     * That is the hitch you feel the moment your finger starts to move.
     *
     * Declaring `will-change` on pointer*down* moves that work to a frame where
     * nothing is animating. It is deliberately not left on permanently: a
     * promoted layer holds its texture in GPU memory, and four full-bleed cards
     * parked there for the life of the page is exactly the trade this is meant
     * to avoid.
     *
     * Suppressing the transitions here too means `paintDrag` only ever writes
     * transforms, instead of re-declaring `transition: none` on seven elements
     * every frame.
     */
    const armGesture = () => {
      for (let d = 0; d < slotCount.value; d++) {
        const el = slotEls[d];
        if (el) {
          el.style.willChange = "transform";
          el.style.transition = "none";
        }
        const dim = dimEls[d];
        if (dim) {
          dim.style.willChange = "opacity";
          dim.style.transition = "none";
        }
      }
    };

    /** Hands the layers back once nothing is moving. */
    const disarmGesture = () => {
      for (let d = 0; d < slotCount.value; d++) {
        const el = slotEls[d];
        if (el) el.style.willChange = "";
        const dim = dimEls[d];
        if (dim) dim.style.willChange = "";
      }
    };

    /**
     * Paints the whole stack for a drag of `dx`/`dy`.
     *
     * The cards behind interpolate one step forward as the front card leaves —
     * the next pass grows into the gap rather than appearing in it, which is
     * what makes a stack feel like it is being dealt from.
     *
     * Transitions are already suppressed by `armGesture`, so this writes nothing
     * but transforms and scrim opacities.
     */
    const paintDrag = () => {
      const progress = Math.min(Math.abs(dx) / DISMISS_DISTANCE, 1);

      const el = slotEls[0];
      if (el) {
        const path = dragPath();
        el.style.transform = `translate3d(${path.x}px, ${path.y}px, 0) rotate(${path.rotate}deg)`;
        // Deliberately transform-only. A fractional opacity here forces the
        // whole card — cover image, `preserve-3d` faces and panel — to be
        // rasterised into an offscreen buffer and alpha-composited every frame,
        // which the compositor cannot do by just moving a cached texture. It costs little on desktop and wrecks
        // the frame rate on a phone.
      }

      for (let d = 1; d < slotCount.value; d++) {
        const behind = slotEls[d];
        if (!behind) continue;
        const eased = d - progress;
        behind.style.transform = depthTransform(eased);
        paintDim(d, depthDim(eased));
      }
    };

    /**
     * Moves the front card from where the finger left it to `to`.
     *
     * A CSS transition rather than a per-frame JS tween. anime.js — or any
     * library — has to write `style.transform` from a callback on every frame,
     * which puts the animation on the main thread: a Vue re-render, an image
     * decode or a GC pause anywhere in the app stalls the card mid-flight. A
     * transition on `transform` is handed to the compositor and keeps its own
     * time regardless, which is the difference between smooth and janky on a
     * phone. The cards behind already moved this way; now the front one does too.
     *
     * The transform string is written whole rather than as separate translate
     * and rotate properties, so nothing else can own half of it.
     */
    const transformOf = (s: { x: number; y: number; rotate: number }) =>
      `translate3d(${s.x}px, ${s.y}px, 0) rotate(${s.rotate}deg)`;

    /**
     * Which instruction the front slot is currently obeying.
     *
     * Every motion here carries a fallback timer, because `transitionend` does
     * not fire in a background tab and the deck must not be able to stall. The
     * catch is that an *interrupted* motion never reports completion either —
     * grabbing a card mid-spring replaces the running transition, so no
     * `transitionend` arrives and the fallback survives its own gesture. It then
     * fires in the middle of whatever came next and runs that gesture's
     * completion work: `springBack` clears the inline transform, which yanks a
     * card that is mid-throw back to the centre of the deck, and the throw's own
     * timer then advances the deck out from under it. That is the flick that
     * springs back and then vanishes.
     *
     * Bumping this invalidates every callback still in flight. Cheaper and
     * harder to get wrong than tracking each timer, since the stale callbacks
     * simply find themselves out of date and do nothing.
     */
    let frontGen = 0;

    const moveFront = (
      to: { x: number; y: number; rotate: number },
      options: {
        spring?: boolean;
        onDone?: () => void;
        /** Overrides the drag position — used when a card flies back *in*. */
        from?: { x: number; y: number; rotate: number };
      },
    ) => {
      const el = slotEls[0];
      if (!el) {
        options.onDone?.();
        return;
      }

      // Anything already moving this slot is now out of date.
      const gen = ++frontGen;

      // Land the starting frame synchronously, with transitions suppressed:
      // waiting a frame would show the card at its resting position first, which
      // on a fly-in reads as a flicker at the destination before the travel.
      el.style.transition = "none";
      el.style.transform = transformOf(options.from ?? dragPath());
      // Force the start to be committed, or the browser coalesces both writes
      // into one style change and there is nothing to transition from.
      void el.offsetHeight;

      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener("transitionend", onEnd);
        // Superseded: something else owns this slot now, so completing would
        // undo whatever it has done rather than finishing this.
        if (gen !== frontGen) return;
        options.onDone?.();
      };
      const onEnd = (e: TransitionEvent) => {
        // Only the card's own transform; children finishing their own
        // transitions must not end the gesture early.
        if (e.target === el && e.propertyName === "transform") done();
      };
      el.addEventListener("transitionend", onEnd);

      el.style.transition = options.spring
        ? // No real spring in CSS, but the overshoot is what the spring was for
          // and a back-out curve gives it for free on the compositor.
          `transform ${RETURN_MS}ms ${RETURN_EASE}`
        : `transform ${THROW_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`;
      el.style.transform = transformOf(to);

      // `transitionend` never fires if the element is removed, the tab is
      // hidden, or the target equals the start. The callers already guard with
      // their own timers; this keeps the listener from leaking either way.
      // `done` checks the generation, so a fallback that outlives its gesture
      // detaches the listener and stops there.
      setTimeout(done, (options.spring ? RETURN_MS : THROW_MS) + 120);
    };

    /** Returns the card to neutral after a drag that was not a throw. */
    const springBack = () => {
      const el = slotEls[0];
      moveFront(
        { x: 0, y: 0, rotate: 0 },
        {
          spring: true,
          onDone: () => {
            if (!el) return;
            el.style.transform = "";
            el.style.opacity = "";
            el.style.transition = "";
          },
        },
      );
      // The cards behind settle on their own transition rather than being
      // driven from here: their resting position is a composed transform
      // (offset, angle and scale together), and one place owning the whole
      // string is what keeps the rotation from being dropped.
      settleBehind((d) => d);
      // `transitionend` does not fire in a background tab, so rest is restored
      // on a timer regardless — but only if this spring is still the thing the
      // card is doing. Unguarded, a spring interrupted by a new flick reset the
      // deck to neutral half a second into the throw that replaced it.
      const gen = frontGen;
      setTimeout(() => {
        if (gen === frontGen) resetSlots();
      }, 900);
    };

    /** Moves every card behind to `depthFor(d)`, letting CSS carry the motion. */
    const settleBehind = (depthFor: (d: number) => number) => {
      for (let d = 1; d < slotCount.value; d++) {
        const behind = slotEls[d];
        if (!behind) continue;
        const target = depthFor(d);
        behind.style.transition = DEPTH_TRANSITION;
        behind.style.transform = depthTransform(target);
        paintDim(d, depthDim(target), DEPTH_TRANSITION);
      }
    };

    const throwOut = (direction: number, fromDrag: boolean) => {
      const el = slotEls[0];
      if (!el || animating) return;
      animating = true;

      // The deck must advance even if the animation never reports completion.
      // `transitionend` does not fire in a background tab — a card thrown just
      // before the user switches away would otherwise stay off-screen and the
      // deck would never move on.
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        advance();
      };

      // The cards behind finish rising while the front one leaves, so the deck
      // is already settled by the time it is out of frame.
      settleBehind((d) => d - 1);

      moveFront(
        {
          x: direction * (window.innerWidth + 220),
          // Leaves along the same shallow band it was dragged in, continuing
          // the arc rather than veering off at a new angle.
          y: fromDrag ? dragPath().y + direction * grabSign * 40 : 0,
          // Not faded out: the card travels a full viewport width past the
          // edge, so it is gone either way, and a fractional opacity on the way
          // there costs the offscreen rasterisation the drag now avoids.
          rotate: direction * grabSign * MAX_ROTATE,
        },
        { onDone: finish },
      );
      // Same rule: only advance the deck if this throw is still the current
      // instruction. A stale one firing here would deal away a card the user
      // has since grabbed.
      const gen = frontGen;
      setTimeout(() => {
        if (gen === frontGen) finish();
      }, THROW_MS + 200);
    };

    /**
     * The opposite of a throw: brings the card that was last dealt away back to
     * the front, flying in from the side it left by.
     *
     * A swipe only ever moves the deck forwards, which is fine for a gesture —
     * you can keep flicking round the loop. A pair of arrows implies both
     * directions, and "back" that silently went forwards would be a lie.
     */
    const retreat = () => {
      if (animating) return;
      animating = true;

      front.value = (front.value - 1 + count.value) % count.value;
      setFlipped(false);
      props.onFrontChange?.(cardAt(0).key);

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        const el = slotEls[0];
        if (el) {
          el.style.transform = "";
          el.style.opacity = "";
          el.style.transition = "";
        }
        animating = false;
      };

      nextTick(() => {
        // Same ordering rule as `advance`: the slots hold their new cards only
        // after the patch, so put the deck at rest here rather than above, then
        // fly the new front card in over the top of it.
        resetSlots();
        moveFront(
          { x: 0, y: 0, rotate: 0 },
          {
            from: {
              x: -(window.innerWidth + 220),
              y: 0,
              rotate: -MAX_ROTATE,
            },
            onDone: finish,
          },
        );
        const gen = frontGen;
        setTimeout(() => {
          if (gen === frontGen) finish();
        }, THROW_MS + 200);
      });
    };

    const release = () => {
      const elapsed = Math.max(performance.now() - startTime, 1);
      const velocity = Math.abs(dx) / elapsed;

      if (Math.abs(dx) > DISMISS_DISTANCE || velocity > DISMISS_VELOCITY) {
        throwOut(dx < 0 ? -1 : 1, true);
        return;
      }
      springBack();
    };

    /** A card with a back turns over; one without reports the tap upwards. */
    const tap = () => {
      const card = cardAt(0);
      if (card.renderBack) setFlipped(!flipped.value);
      else props.onSelect?.(card.key);
    };

    /**
     * The gesture is tracked on the window, not on the card.
     *
     * Pointer capture is supposed to keep events coming after the finger leaves
     * the element, but it fails often enough to matter — a cancelled touch, a
     * pointer the OS takes for its own gesture, an element re-rendered
     * mid-drag. When it fails the card stops receiving moves and never receives
     * its `pointerup`, so it sits wherever the finger abandoned it. Listening on
     * the window means the release always arrives and the card always comes
     * back to neutral.
     */
    const endGesture = () => {
      window.removeEventListener("pointermove", onWindowMove);
      window.removeEventListener("pointerup", onWindowUp);
      window.removeEventListener("pointercancel", onWindowCancel);
      if (paintHandle !== null) cancelAnimationFrame(paintHandle);
      paintHandle = null;
      pointerId = null;
    };

    /**
     * Coalesces moves into one paint per frame.
     *
     * A phone reports pointer moves faster than it draws — 120Hz digitisers
     * against a 60Hz display are ordinary — so painting straight from the
     * handler styles the stack two or three times for a frame that renders once.
     * `dx`/`dy` are already up to date by the time the callback runs, so the
     * dropped paints cost nothing.
     */
    let paintHandle: number | null = null;
    const schedulePaint = () => {
      if (paintHandle !== null) return;
      paintHandle = requestAnimationFrame(() => {
        paintHandle = null;
        if (pointerId !== null && moved) paintDrag();
      });
    };

    const onWindowMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      dx = e.clientX - startX;
      dy = e.clientY - startY;
      if (!moved && Math.hypot(dx, dy) > TAP_SLOP) moved = true;
      if (!moved) return;
      schedulePaint();
    };

    const onWindowUp = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      const wasDrag = moved;
      endGesture();
      if (wasDrag) {
        release();
        return;
      }
      // Nothing moved, so there is nothing to animate back — but the gesture
      // was armed on pointerdown and those layers and suppressed transitions
      // have to be handed back before the card flips.
      resetSlots();
      tap();
    };

    const onWindowCancel = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      const wasDrag = moved;
      endGesture();
      // A cancelled gesture is never a tap, and it must still put the card back.
      if (wasDrag) release();
    };

    const onPointerDown = (e: PointerEvent) => {
      if (animating) return;
      // A new pointer takes over rather than being refused, so a gesture that
      // somehow never ended cannot leave the stack unresponsive.
      endGesture();
      pointerId = e.pointerId;
      moved = false;
      dx = 0;
      dy = 0;
      startX = e.clientX;
      startY = e.clientY;
      startTime = performance.now();
      const bounds = slotEls[0]?.getBoundingClientRect();
      grabSign = bounds && e.clientY > bounds.top + bounds.height / 2 ? -1 : 1;
      // Taking hold of the card supersedes whatever it was doing. Without this
      // the interrupted motion's fallback still fires — and a spring-back's
      // completion work clears the inline transform, dropping a card the finger
      // is holding, or one already thrown, back to the middle of the deck.
      frontGen++;
      // Build the layers now, on a frame where nothing is moving, rather than
      // on the first pointermove.
      armGesture();
      window.addEventListener("pointermove", onWindowMove);
      window.addEventListener("pointerup", onWindowUp);
      window.addEventListener("pointercancel", onWindowCancel);
    };

    onBeforeUnmount(() => {
      endGesture();
      clearTimeout(flipTimer);
    });

    /** Deals the front card away, as the next arrow and a right swipe do. */
    const goNext = () => {
      dx = 0;
      dy = 0;
      grabSign = 1;
      throwOut(1, false);
    };

    /** Keyboard equivalent of the arrows, so the deck is not pointer-only. */
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        if (e.key === "ArrowLeft") retreat();
        else goNext();
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        tap();
      }
    };

    const arrow = (icon: string, label: string, onClick: () => void) => (
      <button
        type="button"
        class="hidden sm:inline-flex btn btn-circle btn-ghost text-base-content/40 hover:text-base-content shrink-0"
        aria-label={label}
        title={label}
        onClick={onClick}
      >
        <span class="material-symbols-outlined text-2xl">{icon}</span>
      </button>
    );

    return () => (
      <div
        class={[
          // A rotated card has a much taller axis-aligned paint box while it
          // is being dragged. Without containment, Chrome includes that box in
          // the document's scrollable overflow, briefly adds a vertical
          // scrollbar, and shifts the centred deck sideways. `overflow: clip`
          // keeps it out of the document's scroll geometry; the viewport-sized
          // clip margin still lets the card paint naturally throughout the
          // visible throw instead of cutting it at the stack's own bounds.
          "overflow-clip",
          props.fill ? "flex h-full min-h-0 flex-col" : "",
        ]}
        style={{ overflowClipMargin: "100vmax" }}
      >
        {/* Arrows flank the card from 600px up. On a phone the gesture already
            covers this and there is no room beside a full-width pass; on a
            pointer device a swipe is a drag nobody thinks to try, so the deck
            needs something obviously clickable. */}
        {/* Generous gap: the cards behind are rotated, so their corners reach
            past the container the front card defines. */}
        <div
          class={[
            "flex items-center justify-center gap-2 sm:gap-6 mb-7",
            // The margin is not decoration: the cards behind are rotated and
            // pushed down, so this is the room their corners peek into before
            // the page's `overflow-hidden` clips them.
            props.fill ? "flex-1 min-h-0" : "",
          ]}
        >
          {count.value > 1 &&
            arrow("chevron_left", t("customer.collected.stack_prev"), retreat)}

          {/* On a phone the pass takes the screen it is given, so the height
              comes from the viewport. On a wide screen that would stretch it
              into a billboard, so it reverts to pass proportions. The slots are
              absolute inside, so the cards behind add nothing to the layout —
              hence the margin on the row for their peek. */}
          <div
            class={[
              "relative",
              props.fill
                ? // Exactly the space left over, measured by the flexbox rather
                  // than guessed at in viewport units.
                  "w-full h-full"
                : "w-full max-w-full aspect-[5/7] sm:flex-none",
            ]}
            style={{ maxWidth: `${props.maxCardWidth ?? 460}px` }}
          >
            {/* Painted back-to-front, so the front slot is last in the DOM and
              sits on top without a z-index on every layer. */}
            {Array.from({ length: slotCount.value })
              .map((_, i, arr) => arr.length - 1 - i)
              .map((depth) => {
                const isFront = depth === 0;
                const card = cardAt(depth);
                // Only a card mid-turn is a 3D object. The rest of the time
                // every slot is a flat subtree the compositor can hold as one
                // texture and simply move — see `flip3d`.
                const is3d = isFront && flip3d.value && !!card.renderBack;
                return (
                  <div
                    key={`slot-${depth}`}
                    ref={setSlot(depth)}
                    role={isFront ? "button" : undefined}
                    tabindex={isFront ? 0 : undefined}
                    aria-hidden={isFront ? undefined : "true"}
                    onPointerdown={isFront ? onPointerDown : undefined}
                    onKeydown={isFront ? onKeydown : undefined}
                    class={[
                      "absolute inset-0 rounded-3xl",
                      isFront
                        ? // Hinged below the card, so the tilt reads as the card
                          // pivoting in the hand rather than spinning about its
                          // middle. `pan-y` not `none`: the browser keeps
                          // vertical scrolling, we take the horizontal axis.
                          "origin-[50%_140%] touch-pan-y cursor-grab active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        : "pointer-events-none",
                    ]}
                    style={
                      isFront
                        ? // Perspective only exists while the card is turning.
                          // As a permanent class it made every slot the root of
                          // a live 3D scene for the whole life of the page.
                          is3d
                          ? { perspective: "1400px" }
                          : undefined
                        : {
                            transform: depthTransform(depth),
                            transition: DEPTH_TRANSITION,
                          }
                    }
                  >
                    {/* Two faces on one card, but only while it is being turned
                        over. The 3D properties are inline rather than utility
                        classes because they have to apply together or the back
                        shows through the front. */}
                    <div
                      class="relative w-full h-full transition-transform duration-500"
                      style={
                        is3d
                          ? {
                              transformStyle: "preserve-3d",
                              transform: flipped.value
                                ? "rotateY(180deg)"
                                : "none",
                            }
                          : undefined
                      }
                    >
                      <div
                        class="absolute inset-0"
                        style={
                          is3d ? { backfaceVisibility: "hidden" } : undefined
                        }
                      >
                        {card.render(isFront)}
                      </div>
                      {/* The back is mounted for the turn and unmounted after
                          it. It is a second complete pass — its own photograph,
                          panel and QR — so keeping it permanently
                          face-down doubled the front card for something only a
                          tap ever reveals. */}
                      {is3d && (
                        <div
                          class="absolute inset-0"
                          style={{
                            backfaceVisibility: "hidden",
                            transform: "rotateY(180deg)",
                          }}
                        >
                          {card.renderBack!()}
                        </div>
                      )}
                    </div>

                    {/* Depth by dimming as well as scale: on a small screen the
                        size difference alone is too subtle to read as "behind".
                        A solid scrim rather than a filter on the slot — same
                        look, one cacheable rectangle instead of re-rendering the
                        whole card offscreen every frame. */}
                    <div
                      ref={setDim(depth)}
                      aria-hidden="true"
                      class="absolute inset-0 rounded-3xl bg-black pointer-events-none"
                      style={{
                        opacity: String(isFront ? 0 : depthDim(depth)),
                        transition: DEPTH_TRANSITION,
                      }}
                    />
                  </div>
                );
              })}
          </div>

          {count.value > 1 &&
            arrow("chevron_right", t("customer.collected.stack_next"), goNext)}
        </div>

        <div class="flex items-center justify-center gap-2.5 mt-2.5 shrink-0">
          <p class="font-mono text-[10px] text-base-content/40 tabular-nums">
            {t("customer.collected.stack_position", [
              front.value + 1,
              count.value,
            ])}
          </p>
          <div class="flex gap-1.5">
            {props.items.map((item, i) => (
              <span
                key={item.key}
                class={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${
                  i === front.value
                    ? "w-4 bg-base-content/50"
                    : "w-1.5 bg-base-content/20"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  },
});
